import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/cn';

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  indeterminate?: boolean;
  /**
   * Renders the box inside a `<label>`, so the text is part of the hit target.
   * Every call site wanted this, and hand-rolling it meant some pages shipped a
   * 16px tap target next to unclickable text.
   */
  label?: ReactNode;
  /** Boolean-first alternative to `onChange`, matching `Switch`. */
  onCheckedChange?: (checked: boolean) => void;
};

/**
 * Checkbox built on a real `<input type="checkbox">` — the native element keeps
 * form semantics, keyboard behaviour and the indeterminate state for free; the
 * visual box is drawn on top.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, indeterminate, checked, label, onCheckedChange, onChange, disabled, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const box = (
    <span className={cn('relative inline-flex h-4 w-4 shrink-0 items-center justify-center', !label && className)}>
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none rounded border border-input bg-surface transition-colors checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary focus-ring disabled:cursor-not-allowed disabled:opacity-50"
        aria-checked={indeterminate ? 'mixed' : undefined}
        onChange={(event) => {
          onChange?.(event);
          onCheckedChange?.(event.target.checked);
        }}
        {...rest}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none relative text-primary-foreground',
          indeterminate ? 'block' : 'hidden peer-checked:block',
        )}
      >
        {indeterminate ? <Minus className="h-3 w-3" /> : <Check className="h-3 w-3" />}
      </span>
    </span>
  );

  if (!label) return box;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 text-xs',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {box}
      <span>{label}</span>
    </label>
  );
});
